import * as React from "react";
import { Handle, NodeToolbar, Position, type NodeProps } from "@xyflow/react";
import { FileChartLine, Layers, Link2, Play, Plus, Shuffle, type LucideIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import {
  WORKSPACE_PLACEHOLDER_NODE_INNER_SIZE,
  WORKSPACE_PLACEHOLDER_NODE_SIZE,
} from "../workspaceGraphConstants";
import type { WorkspacePlaceholderNodeData } from "../workspacePlaceholderUtils";
import type { WorkspaceChildActionKind } from "../workspaceChildActions";
import { workspaceChildActionsForNode } from "../workspaceChildActions";
import {
  useWorkspaceNodeToolbarSide,
  WORKSPACE_NODE_TOOLBAR_OFFSET_PX,
} from "../useWorkspaceNodeToolbarSide";

const EDGE_HANDLE_CLASS =
  "!pointer-events-none !h-px !w-px !min-h-0 !min-w-0 !border-0 !bg-transparent !opacity-0";

const ACTION_ICONS: Record<WorkspaceChildActionKind, LucideIcon> = {
  strategy: Layers,
  report: FileChartLine,
  simulation: Play,
};

/** Tint matches real node type icons (violet / sky / glacial green / teal). */
const ACTION_ICON_CLASS: Record<WorkspaceChildActionKind, string> = {
  strategy: "text-violet-600 dark:text-violet-300",
  report: "text-sky-600 dark:text-sky-300",
  simulation: "text-[color:var(--tessera-pnl-positive)]",
};

function uniqueActionKinds(
  actions: WorkspacePlaceholderNodeData["actions"]
): WorkspaceChildActionKind[] {
  return Array.from(new Set(actions.map((action) => action.kind)));
}

function placeholderNextKind(
  flowData: WorkspacePlaceholderNodeData
): WorkspaceChildActionKind | "combined_simulation" | null {
  if (flowData.parentNode.type === "combined_policy") return "combined_simulation";
  if (flowData.parentNode.type === "strategy") return "report";
  if (flowData.parentNode.type === "report") return "simulation";
  const kinds = uniqueActionKinds(flowData.actions);
  return kinds.length === 1 ? kinds[0]! : null;
}

function placeholderActionIcon(flowData: WorkspacePlaceholderNodeData): {
  icon: LucideIcon;
  iconClassName: string;
} {
  const next = placeholderNextKind(flowData);
  if (next === "combined_simulation") {
    return { icon: Shuffle, iconClassName: "text-teal-700 dark:text-teal-300" };
  }
  if (next != null) {
    return {
      icon: ACTION_ICONS[next] ?? Plus,
      iconClassName: ACTION_ICON_CLASS[next] ?? "text-muted-foreground",
    };
  }
  return { icon: Plus, iconClassName: "text-muted-foreground" };
}

export function WorkspacePlaceholderNode({ data }: NodeProps): React.ReactElement {
  const flowData = data as WorkspacePlaceholderNodeData;
  const menuOpen = Boolean(flowData.showActionsMenu);
  const disabled = Boolean(flowData.mutating || flowData.parentNode.isDisabled);
  const isCombinedPolicy = flowData.parentNode.type === "combined_policy";
  const { icon: ActionIcon, iconClassName } = placeholderActionIcon(flowData);
  const toolbarSide = useWorkspaceNodeToolbarSide(menuOpen && !isCombinedPolicy);
  // Prefer create+existing pairs from the parent node type (not the single "next" kind only).
  const menuActions = workspaceChildActionsForNode(flowData.parentNode).filter((action) =>
    flowData.actions.some((entry) => entry.kind === action.kind)
  );

  const activate = () => {
    if (disabled) return;
    if (isCombinedPolicy) {
      flowData.onRunCombinedSimulation?.(flowData.parentNode);
      return;
    }
    flowData.onToggleMenu?.();
  };

  return (
    <>
      <div
        className="relative flex items-center justify-center"
        style={{ width: WORKSPACE_PLACEHOLDER_NODE_SIZE, height: WORKSPACE_PLACEHOLDER_NODE_SIZE }}
      >
        <Handle
          type="target"
          position={Position.Left}
          id="target"
          className={EDGE_HANDLE_CLASS}
          style={{ left: 0, top: "50%", transform: "translate(-50%, -50%)" }}
        />

        <div
          role="button"
          tabIndex={disabled ? -1 : 0}
          data-workspace-node=""
          data-workspace-node-variant="placeholder-card"
          data-workspace-placeholder-kind={isCombinedPolicy ? "combined-simulation" : "next-step"}
          title={flowData.label}
          aria-label={flowData.label}
          aria-disabled={disabled || undefined}
          aria-expanded={isCombinedPolicy ? undefined : menuOpen}
          className={cn(
            "nodrag nopan flex items-center justify-center",
            disabled && "pointer-events-none"
          )}
          style={{ width: WORKSPACE_PLACEHOLDER_NODE_SIZE, height: WORKSPACE_PLACEHOLDER_NODE_SIZE }}
          onMouseDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.stopPropagation();
            activate();
          }}
          onKeyDown={(event) => {
            if (disabled) return;
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              event.stopPropagation();
              activate();
            }
          }}
        >
          <div
            className={cn(
              "flex rotate-45 items-center justify-center rounded-lg border border-border bg-card text-card-foreground shadow-sm transition-shadow",
              "hover:border-primary/55",
              menuOpen &&
                !isCombinedPolicy &&
                "border-primary/55 ring-2 ring-primary/45 ring-offset-1 ring-offset-background",
              disabled && "opacity-50",
              "dark:border-input dark:bg-sidebar dark:text-sidebar-foreground"
            )}
            style={{
              width: WORKSPACE_PLACEHOLDER_NODE_INNER_SIZE,
              height: WORKSPACE_PLACEHOLDER_NODE_INNER_SIZE,
            }}
          >
            <ActionIcon
              className={cn("size-4 -rotate-45", iconClassName)}
              strokeWidth={1.9}
              aria-hidden
            />
          </div>
        </div>
      </div>

      {!isCombinedPolicy ? (
        <NodeToolbar
          isVisible={menuOpen}
          position={toolbarSide.position}
          align={toolbarSide.align}
          offset={WORKSPACE_NODE_TOOLBAR_OFFSET_PX}
          className="nodrag nopan nowheel"
        >
          <div
            data-workspace-node-actions=""
            data-workspace-placeholder-menu=""
            data-slot="dropdown-menu-content"
            role="menu"
            className="nodrag nopan nowheel z-[1200] min-w-56 overflow-visible rounded-[6px] bg-popover p-1 text-popover-foreground shadow-md ring-1 ring-foreground/10"
            onMouseDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="px-2 py-1.5" data-workspace-node-actions-header="">
              <div className="flex flex-col gap-0.5">
                <span
                  data-workspace-node-actions-type=""
                  className="font-[family-name:var(--font-primary)] text-[10px] uppercase tracking-wide text-[color:var(--brand-tropical-surf)]"
                >
                  Next
                </span>
                <span
                  data-workspace-node-actions-title=""
                  className="whitespace-nowrap font-[family-name:var(--font-primary)] text-sm font-medium text-[color:var(--brand-tropical-surf)]"
                >
                  {flowData.label}
                </span>
              </div>
            </div>
            <div className="-mx-1 my-1 h-px bg-border" />
            {menuActions.map((action) => {
              const ActionIcon =
                action.mode === "existing" ? Link2 : ACTION_ICONS[action.kind] ?? Plus;
              return (
                <button
                  key={`${action.kind}:${action.mode}`}
                  type="button"
                  role="menuitem"
                  data-slot="dropdown-menu-item"
                  disabled={disabled}
                  className={cn(
                    "relative flex w-full cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none",
                    "hover:bg-accent hover:text-accent-foreground",
                    "disabled:pointer-events-none disabled:opacity-50"
                  )}
                  onClick={() =>
                    flowData.onAddChild?.(flowData.parentNode, action.kind, action.mode)
                  }
                >
                  <ActionIcon className="size-4 shrink-0" />
                  <span className="whitespace-nowrap">{action.label}</span>
                </button>
              );
            })}
          </div>
        </NodeToolbar>
      ) : null}
    </>
  );
}
